import * as go from 'gojs';
import {
  SMART_SELECT_OPTION_CARD_HEIGHT,
  SMART_SELECT_OPTION_CARD_WIDTH,
  SMART_SELECT_OPTIONS_PER_PAGE,
  SMART_SELECT_PANEL_WIDTH,
  SMART_SELECT_PLACE_BUTTON_FILL,
  SMART_SELECT_PREVIEW_BACKGROUND_FILL,
} from './smartSelectConstants';
import {
  getCachedSmartSelectOptions,
  getSmartSelectRecommendationStatus,
} from './smartSelectRecommendationLoader';
import type { SmartSelectRecommendedOption } from './smartSelectRecommendations';
import {
  clearTimer,
  hideTimers,
  nodeSmartSelectSelectedOptions,
  nodeSmartSelectSelectedPortKeys,
} from './smartSelectState';

const $ = go.GraphObject.make;

export interface SmartSelectOptionActions {
  toggleSmartSelectOption: (
    node: go.Node,
    option: SmartSelectRecommendedOption,
  ) => void;
}

export const createSmartSelectOptionCards = ({
  toggleSmartSelectOption,
}: SmartSelectOptionActions) => {
  const createSmartSelectOptionImage = (imageSource: string | null) =>
    $(
      go.Panel,
      'Auto',
      {
        width: 82,
        height: 76,
        margin: new go.Margin(0, 0, 12, 0),
        alignment: go.Spot.Left,
      },
      $(go.Shape, 'Rectangle', {
        width: 82,
        height: 76,
        fill: SMART_SELECT_PREVIEW_BACKGROUND_FILL,
        stroke: 'transparent',
        strokeWidth: 0,
      }),
      ...(imageSource
        ? [
            $(go.Picture, {
              source: imageSource,
              desiredSize: new go.Size(74, 68),
              imageStretch: go.GraphObject.Uniform,
              alignment: go.Spot.Center,
            }),
          ]
        : []),
    );

  const createSmartSelectOptionTextLine = (
    text: string,
    font = '400 13px Sandvik Sans Text',
    stroke = 'rgba(0, 0, 0, 0.77)',
  ) =>
    $(go.TextBlock, {
      text,
      width: SMART_SELECT_OPTION_CARD_WIDTH,
      height: 18,
      maxLines: 1,
      overflow: go.TextBlock.OverflowEllipsis,
      wrap: go.TextBlock.None,
      font,
      stroke,
      margin: new go.Margin(0, 0, 5, 0),
      alignment: go.Spot.Left,
      verticalAlignment: go.Spot.Center,
    });

  const createSmartSelectPlaceButton = (
    node: go.Node,
    option: SmartSelectRecommendedOption,
  ) => {
    const selectedPortKey = nodeSmartSelectSelectedPortKeys.get(node);
    const isPlaced = Boolean(
      selectedPortKey &&
      nodeSmartSelectSelectedOptions.get(node)?.get(selectedPortKey) === option,
    );
    const handlePlaceAction = (event: go.InputEvent) => {
      event.handled = true;

      toggleSmartSelectOption(node, option);
    };

    const handlePlaceActionDown = (event: go.InputEvent) => {
      event.handled = true;
      clearTimer(hideTimers, node);
    };

    return $(
      go.Panel,
      'Auto',
      {
        alignment: go.Spot.Left,
        cursor: 'pointer',
        margin: new go.Margin(8, 0, 0, 0),
        isActionable: true,
        actionDown: handlePlaceActionDown,
        actionUp: handlePlaceAction,
        click: handlePlaceAction,
      },
      $(go.Shape, 'RoundedRectangle', {
        height: 32,
        parameter1: 16,
        fill: isPlaced
          ? SMART_SELECT_PREVIEW_BACKGROUND_FILL
          : SMART_SELECT_PLACE_BUTTON_FILL,
        stroke: 'transparent',
        strokeWidth: 0,
        isActionable: true,
        actionDown: handlePlaceActionDown,
        actionUp: handlePlaceAction,
        click: handlePlaceAction,
      }),
      $(
        go.Panel,
        'Horizontal',
        {
          margin: new go.Margin(6, 14),
          isActionable: true,
          actionDown: handlePlaceActionDown,
          actionUp: handlePlaceAction,
          click: handlePlaceAction,
        },
        $(go.TextBlock, {
          text: isPlaced ? '-' : '+',
          height: 20,
          font: '400 16px Sandvik Sans Text',
          stroke: isPlaced ? 'rgba(0, 0, 0, 0.77)' : 'white',
          margin: new go.Margin(0, 8, 0, 0),
          verticalAlignment: go.Spot.Center,
          isActionable: true,
          actionDown: handlePlaceActionDown,
          actionUp: handlePlaceAction,
          click: handlePlaceAction,
        }),
        $(go.TextBlock, {
          text: isPlaced ? 'Remove' : 'Place',
          height: 20,
          font: '400 13px Sandvik Sans Text',
          stroke: isPlaced ? 'rgba(0, 0, 0, 0.77)' : 'white',
          verticalAlignment: go.Spot.Center,
          isActionable: true,
          actionDown: handlePlaceActionDown,
          actionUp: handlePlaceAction,
          click: handlePlaceAction,
        }),
      ),
    );
  };

  const createSmartSelectOptionCard = (
    node: go.Node,
    option: SmartSelectRecommendedOption,
    index: number,
  ) =>
    $(
      go.Panel,
      'Vertical',
      {
        width: SMART_SELECT_OPTION_CARD_WIDTH,
        height: SMART_SELECT_OPTION_CARD_HEIGHT,
        margin: new go.Margin(
          0,
          index === SMART_SELECT_OPTIONS_PER_PAGE - 1 ? 0 : 24,
          0,
          0,
        ),
        alignment: go.Spot.TopLeft,
      },
      createSmartSelectOptionImage(option.imageSource),
      createSmartSelectOptionTextLine(
        option.title,
        '600 14px Sandvik Sans Text',
      ),
      createSmartSelectOptionTextLine(option.equipmentType),
      ...option.specLines
        .slice(0, 3)
        .map((line) => createSmartSelectOptionTextLine(line)),
      createSmartSelectOptionTextLine(
        `Basis: ${option.recommendationBasis}`,
        '400 12px Sandvik Sans Text',
        'rgba(0, 0, 0, 0.55)',
      ),
      createSmartSelectPlaceButton(node, option),
    );

  const createSmartSelectRecommendationStatusText = (
    text: string,
    centered = false,
  ) =>
    $(go.TextBlock, {
      text,
      width: SMART_SELECT_PANEL_WIDTH - 168,
      height: centered ? SMART_SELECT_OPTION_CARD_HEIGHT : 36,
      font: centered
        ? 'italic 400 13px Sandvik Sans Text'
        : '400 13px Sandvik Sans Text',
      stroke: 'rgba(0, 0, 0, 0.55)',
      alignment: centered ? go.Spot.Center : go.Spot.Left,
      textAlign: centered ? 'center' : 'left',
      verticalAlignment: go.Spot.Center,
    });

  const getSmartSelectRecommendationElements = (
    node: go.Node,
    portKey: string,
    page = 0,
  ) => {
    const status = getSmartSelectRecommendationStatus(node, portKey);
    const options = getCachedSmartSelectOptions(node, portKey);

    if (status === 'loading') {
      return [
        createSmartSelectRecommendationStatusText('Loading suggestions...'),
      ];
    }

    if (status === 'error') {
      return [
        createSmartSelectRecommendationStatusText(
          'Could not load suggestions for this output.',
        ),
      ];
    }

    if (options.length === 0) {
      return [
        createSmartSelectRecommendationStatusText(
          'No approved-flow suggestions for this output.',
          true,
        ),
      ];
    }

    return options
      .slice(
        page * SMART_SELECT_OPTIONS_PER_PAGE,
        (page + 1) * SMART_SELECT_OPTIONS_PER_PAGE,
      )
      .map((option, index) => createSmartSelectOptionCard(node, option, index));
  };

  return { getSmartSelectRecommendationElements };
};
